import { useEffect, useState, useCallback } from "react";
import { api, fmtMoney } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Checkbox } from "../../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { ScanSearch, Plus, Trash2, FileText, Calculator } from "lucide-react";
import { toast } from "sonner";

const SEV_CLS = {
    high: "bg-red-50 text-red-700 border-red-200",
    medium: "bg-amber-50 text-amber-700 border-amber-200",
    low: "bg-neutral-50 text-neutral-600 border-neutral-200",
};
const STATUSES = [{ v: "open", label: "Open" }, { v: "reviewed", label: "Reviewed" }, { v: "dismissed", label: "Dismissed" }];
const NONE = "__none__";
const FIELDS = [["revenue", "Revenue"], ["gross_profit", "Gross profit"], ["ebitda", "EBITDA"], ["debtors", "Debtors"], ["creditors", "Creditors"], ["stock", "Stock"], ["cash", "Cash"]];

function FinancialsEditor({ dealId, onSaved }) {
    const [rows, setRows] = useState([]);
    const [saving, setSaving] = useState(false);
    useEffect(() => { api.get(`/dd/deal/${dealId}/financials`).then((r) => setRows(r.data.periods || [])); }, [dealId]);

    const set = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
    const save = async () => {
        setSaving(true);
        try {
            const periods = rows.map((r) => {
                const o = { period_end: r.period_end };
                FIELDS.forEach(([k]) => { o[k] = r[k] === "" || r[k] == null ? null : parseFloat(r[k]); });
                return o;
            });
            const { data } = await api.put(`/dd/deal/${dealId}/financials`, { periods });
            setRows(data.periods);
            toast.success("Period financials saved");
            onSaved?.();
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not save — check the dates");
        } finally { setSaving(false); }
    };

    return (
        <details className="border border-border rounded-md bg-white" data-testid="dd-financials">
            <summary className="px-4 py-3 cursor-pointer text-sm font-medium flex items-center gap-2"><Calculator className="w-4 h-4 text-gold" /> Period financials <span className="text-xs text-muted-foreground font-normal">— year-by-year figures the trend checks use ({rows.length} period{rows.length === 1 ? "" : "s"})</span></summary>
            <div className="p-4 pt-0 space-y-3 overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                    <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        <th className="text-left py-1">Period end</th>{FIELDS.map(([k, l]) => <th key={k} className="text-right py-1 px-1">{l}</th>)}<th /></tr></thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td className="pr-1"><Input type="date" className="h-8" value={r.period_end || ""} onChange={(e) => set(i, "period_end", e.target.value)} /></td>
                                {FIELDS.map(([k]) => <td key={k} className="px-1"><Input type="number" className="h-8 font-mono text-right" value={r[k] ?? ""} onChange={(e) => set(i, k, e.target.value)} /></td>)}
                                <td><Button size="icon" variant="ghost" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></Button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setRows((rs) => [...rs, { period_end: "" }])} data-testid="dd-add-period"><Plus className="w-3.5 h-3.5 mr-1" /> Add period</Button>
                    <Button size="sm" onClick={save} disabled={saving} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="dd-save-periods">{saving ? "Saving…" : "Save periods"}</Button>
                </div>
            </div>
        </details>
    );
}

function Evidence({ a, currency }) {
    if (a.source === "document") {
        return (
            <div className="text-xs mt-1">
                <div className="flex items-center gap-1 text-muted-foreground"><FileText className="w-3 h-3" /> {a.evidence.filename}</div>
                <blockquote className="border-l-2 border-gold pl-2 mt-1 italic text-foreground/80">“{a.evidence.quote}”</blockquote>
            </div>
        );
    }
    const e = a.evidence || {};
    const bits = Object.entries(e).filter(([k, v]) => v != null && !["adjustment_id"].includes(k)).map(([k, v]) => (
        <span key={k} className="inline-block mr-3"><span className="text-muted-foreground">{k.replace(/_/g, " ")}:</span> <span className="font-mono">{typeof v === "number" && Math.abs(v) >= 1000 ? fmtMoney(v, currency) : String(v)}</span></span>));
    return (<div className="text-xs mt-1">{bits}{a.detail && <div className="text-muted-foreground mt-1">{a.detail}</div>}</div>);
}

export default function AnomaliesPanel({ dealId, deal, onChanged }) {
    const [data, setData] = useState({ items: [], summary: null });
    const [checklist, setChecklist] = useState([]);
    const [includeDocs, setIncludeDocs] = useState(true);
    const [running, setRunning] = useState(false);
    const [run, setRun] = useState(null);
    const [filter, setFilter] = useState("open");

    const load = useCallback(async () => {
        const [a, c] = await Promise.all([api.get(`/dd/deal/${dealId}/anomalies`), api.get(`/dd/deal/${dealId}/checklist`)]);
        setData(a.data); setChecklist(c.data.items);
    }, [dealId]);
    useEffect(() => { load(); }, [load]);

    const runScan = async () => {
        setRunning(true);
        try {
            const { data } = await api.post(`/dd/deal/${dealId}/anomalies/run`, { include_documents: includeDocs });
            setData({ items: data.items, summary: data.summary }); setRun(data.run);
            onChanged?.();
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
        finally { setRunning(false); }
    };
    const patch = async (id, body) => {
        try { await api.patch(`/dd/anomalies/${id}`, body); await load(); onChanged?.(); }
        catch (err) { toast.error(err.response?.data?.detail || err.message); }
    };

    const s = data.summary;
    const shown = data.items.filter((a) => filter === "all" || a.status === filter);

    return (
        <div className="space-y-4" data-testid="dd-anomalies">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="font-display font-bold tracking-tight flex items-center gap-2"><ScanSearch className="w-4 h-4 text-gold" /> Financial anomalies</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                        Numeric checks run on your EBITDA add-backs and period financials; document review reads uploaded PDFs, Word and text files, and every flag quotes the passage it came from.
                        These are internal — they never appear in the memorandum. There is deliberately no overall risk score.
                    </p>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                    <Button onClick={runScan} disabled={running} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="dd-run-btn">{running ? "Scanning…" : "Run analysis"}</Button>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <Checkbox checked={includeDocs} onCheckedChange={(v) => setIncludeDocs(!!v)} /> Include data-room documents
                    </label>
                </div>
            </div>

            <FinancialsEditor dealId={dealId} />

            {run && (
                <div className="text-xs border border-border rounded-md bg-neutral-50 p-3 space-y-1" data-testid="dd-run-summary">
                    <div>{run.new} new · {run.refreshed} refreshed · {run.cleared} cleared · {run.period_count} period{run.period_count === 1 ? "" : "s"} used</div>
                    {run.documents_requested && !run.ai_available && <div className="text-amber-700">AI review isn't configured, so only the numeric checks ran.</div>}
                    {run.ai_available && run.documents_requested && <div>{run.documents_scanned} document{run.documents_scanned === 1 ? "" : "s"} reviewed.</div>}
                    {run.documents_skipped.map((d, i) => <div key={i} className="text-muted-foreground">Skipped {d.filename} — {d.reason}</div>)}
                    {run.period_count < 2 && <div className="text-muted-foreground">Add at least two periods to enable year-on-year checks.</div>}
                </div>
            )}

            {s && (
                <div className="flex gap-2 items-center">
                    {["open", "reviewed", "dismissed", "all"].map((f) => (
                        <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 rounded-full text-xs border ${filter === f ? "bg-foreground text-background" : "bg-white hover:bg-neutral-50"}`}>
                            {f[0].toUpperCase() + f.slice(1)}{f !== "all" ? ` (${s[f]})` : ""}
                        </button>
                    ))}
                    {s.open > 0 && <span className="ml-2 text-xs text-muted-foreground">Open: {["high", "medium", "low"].filter((k) => s.open_by_severity[k]).map((k) => `${s.open_by_severity[k]} ${k}`).join(", ")}</span>}
                </div>
            )}

            {shown.length === 0 ? (
                <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-md">
                    {data.items.length === 0 ? "Nothing analysed yet. Enter EBITDA add-backs and period financials, then run the analysis." : "Nothing in this view."}
                </div>
            ) : (
                <div className="space-y-2">
                    {shown.map((a) => (
                        <div key={a.id} className="border border-border rounded-md bg-white p-4" data-testid={`dd-anomaly-${a.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium border ${SEV_CLS[a.severity]}`}>{a.severity}</span>
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{a.category.replace(/_/g, " ")} · {a.source}</span>
                                    </div>
                                    <div className="font-medium mt-1">{a.title}</div>
                                    <Evidence a={a} currency={deal.currency} />
                                </div>
                                <div className="flex flex-col gap-2 shrink-0 w-44">
                                    <Select value={a.status} onValueChange={(v) => patch(a.id, { status: v })}>
                                        <SelectTrigger className="h-8" data-testid={`dd-anomaly-status-${a.id}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>{STATUSES.map((x) => <SelectItem key={x.v} value={x.v}>{x.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                    <Select value={a.checklist_item_id || NONE} onValueChange={(v) => patch(a.id, { checklist_item_id: v === NONE ? "" : v })}>
                                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Attach to request…" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={NONE}>Not attached</SelectItem>
                                            {checklist.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <Input className="mt-2 h-8 text-xs" placeholder="Note — e.g. explanation to give the buyer" defaultValue={a.note || ""}
                                onBlur={(e) => e.target.value !== (a.note || "") && patch(a.id, { note: e.target.value })} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
